import { Navigate } from 'react-router-dom';
import { resolveRunMode } from '../../../types/prioritization/session';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import {
  asyncRoomCanShowOptions,
  isConfigSession,
  isRankingSession,
  isValueSession,
} from '../utils/sessionHelpers';
import { ConfigContributorParticipant } from './ConfigContributorParticipant';
import { ParticipantAsyncToolRoom } from './ParticipantAsyncToolRoom';

interface SessionRoomRouterProps {
  session: PrioritizationSession;
  shell: 'standalone' | 'prioritization';
  onBack: () => void;
  onSubmitted: () => void;
}

export function SessionRoomRouter({
  session,
  shell: _shell,
  onBack,
  onSubmitted,
}: SessionRoomRouterProps) {
  const runMode = resolveRunMode(session.modeConfig);

  if (isRankingSession(session)) {
    // Live ranking: redirect to the production live board.
    // Use <Navigate> (declarative) not navigate() (imperative during render) — React 18 safe.
    // No ?startRanking=1 here — participants just wait for the facilitator to open the session.
    // The facilitator triggers auto-create directly from the Event Designer "Go live" button.
    // An open session whose options were typed for it (an offline blueprint)
    // is answered there too: the open room cannot show those options.
    if (runMode !== 'open' || !asyncRoomCanShowOptions(session)) {
      return <Navigate to={`/prioritization/sessions/${session.id}/live`} replace />;
    }

    return (
      <ParticipantAsyncToolRoom
        session={session}
        onBack={onBack}
        onComplete={onSubmitted}
      />
    );
  }

  /**
   * A values step with a scoring tool goes to the tool (defect, 2026-09-03).
   *
   * `ConfigContributorParticipant` collects values through `BatchValueEntry`,
   * which finds work to do only from `session.itemEligibility` — missing
   * ACCEPTED ATTRIBUTE VALUES in the item registry, keyed by attribute key. A
   * session whose criteria were authored inside it (`crit_*` ids) has nothing
   * addressable there, so the participant was told "All candidates have values,
   * ready to proceed" on the step whose whole purpose was for them to enter
   * those values — a dead end for every S4 run with authored criteria.
   *
   * The session already names the instrument that can do this
   * (`selectedToolId`), and the tool resolves the session's own criteria and
   * their human names via `resolveSessionScoringCriteria`, writing one response
   * per participant — which is what the submission tracker on this screen has
   * been counting all along. Route to it.
   *
   * Without a tool there is nothing better to offer, so the config surface
   * still handles that case; it now states plainly when it cannot address the
   * session's criteria instead of reporting them complete.
   */
  if (isValueSession(session) && session.selectedToolId) {
    return (
      <ParticipantAsyncToolRoom
        session={session}
        onBack={onBack}
        onComplete={onSubmitted}
      />
    );
  }

  if (isValueSession(session) || isConfigSession(session)) {
    return <ConfigContributorParticipant session={session} onBack={onBack} />;
  }

  return (
    <ParticipantAsyncToolRoom
      session={session}
      onBack={onBack}
      onComplete={onSubmitted}
    />
  );
}
